/**
 * Maps a Career Hub sub-page on one segment to its closest equivalent on
 * another segment. Used by the admin segment-preview switcher so that
 * switching segments lands on a comparable surface instead of always
 * dumping the user on /career-hub.
 *
 * Each entry is an "equivalence cluster" — pages that fulfil a similar
 * job-to-be-done across segments. If a target segment has no entry in
 * the cluster, the caller falls back to /career-hub.
 */
import type { UserSegment } from "@/contexts/UserProfileContext";

type SegmentRoutes = Partial<Record<UserSegment, string>>;

/**
 * Each cluster lists, per segment, the canonical sub-page that serves
 * the same intent. Keep the routes in sync with publicRoutes.tsx.
 */
const EQUIVALENCE_CLUSTERS: SegmentRoutes[] = [
  // "What should I prepare for next?" — exam prep ↔ placement prep
  {
    school_student: "/career-hub/exam-prep",
    college_student: "/career-hub/placement-prep",
  },
  // "Where am I going after this stage?" — colleges ↔ higher education
  {
    school_student: "/career-hub/colleges",
    college_student: "/career-hub/higher-education",
  },
  // "Real-world application" — admissions (school) ↔ internships (college)
  {
    school_student: "/career-hub/admissions",
    college_student: "/career-hub/internships",
  },
  // School-only: stream guidance — no college/working equivalent yet.
  // College-only: events — no school/working equivalent yet.
  // Working professional currently has no segment-specific sub-pages,
  // so all of its switches will fall through to /career-hub.
];

/**
 * Find the closest equivalent route for `targetSegment` given the user's
 * current path. Returns null when no equivalent exists; the caller should
 * then fall back to /career-hub.
 *
 * Matching is by exact pathname (ignoring query/hash) to keep the mapping
 * unambiguous and easy to audit.
 */
export const findEquivalentRoute = (
  currentPath: string,
  targetSegment: UserSegment,
): string | null => {
  const cluster = EQUIVALENCE_CLUSTERS.find((c) =>
    Object.values(c).some((p) => p === currentPath),
  );
  if (!cluster) return null;
  return cluster[targetSegment] ?? null;
};

/** Exposed for tests. */
export const __EQUIVALENCE_CLUSTERS__ = EQUIVALENCE_CLUSTERS;
